// Linha de produto (montada em pages/Reports.js):
//   { id, name, category, photo_url, cmv, cmvPct, venda, lucro, margin,
//     soldRevenue, soldCount, salesShare, unidade, custoVendido? }
// cmv/venda/lucro são por unidade (por kg/L no produto a peso); soldRevenue/soldCount/salesShare são dos últimos 30 dias.
import { totaisPorMedida } from "../../../lib/unidadeVenda";

export const SEM_CATEGORIA = "Sem categoria";

export function ordenar(linhas, { key, dir }) {
  const sinal = dir === "asc" ? 1 : -1;
  return [...linhas].sort((a, b) => {
    let va = a[key];
    let vb = b[key];
    if (typeof va === "string" || typeof vb === "string") {
      va = String(va || "").toLowerCase();
      vb = String(vb || "").toLowerCase();
    }
    return va < vb ? -sinal : va > vb ? sinal : 0;
  });
}

const soma = (lista, fn) => lista.reduce((s, x) => s + fn(x), 0);
const media = (lista, fn) => (lista.length ? soma(lista, fn) / lista.length : 0);

/**
 * Resumo de uma categoria. Só entram na conta os produtos com ficha técnica
 * (venda e custo > 0) — produto sem ficha tem custo 0 e puxaria o CMV para baixo,
 * a mesma regra dos cards do topo.
 *
 * Com vendas em 30 dias o CMV é o de verdade: custo das unidades vendidas
 * (custo da ficha × quantidade) sobre o que elas faturaram. Sem vendas, cai na
 * média por item do cardápio da categoria (`porItem: true`).
 */
export function resumirCategoria(nome, produtos) {
  const comFicha = produtos.filter((p) => p.venda > 0 && p.cmv > 0);
  const vendidos = comFicha.filter((p) => p.soldCount > 0 && p.soldRevenue > 0);
  const base = {
    id: `cat:${nome}`,
    name: nome,
    produtos,
    qtdProdutos: produtos.length,
    semFicha: produtos.length - comFicha.length,
    salesShare: soma(produtos, (p) => p.salesShare),
    soldRevenue: soma(produtos, (p) => p.soldRevenue),
  };
  if (vendidos.length) {
    // Pizza traz o custo já somado tamanho a tamanho (custoVendido, lib/pizza.js)
    const custo = soma(vendidos, (p) => p.custoVendido ?? p.cmv * p.soldCount);
    const faturamento = soma(vendidos, (p) => p.soldRevenue);
    const cmvPct = (custo / faturamento) * 100;
    return {
      ...base, porItem: false, unidades: soma(vendidos, (p) => p.soldCount),
      // Quilo e litro à parte, para a legenda "12 un · 3,500 kg vendidos" (30/09/2026).
      vendidosPorMedida: totaisPorMedida(vendidos, "soldCount"),
      cmv: custo, venda: faturamento, lucro: faturamento - custo, cmvPct, margin: 100 - cmvPct,
    };
  }
  if (comFicha.length) {
    return {
      ...base, porItem: true, unidades: 0,
      cmv: media(comFicha, (p) => p.cmv), venda: media(comFicha, (p) => p.venda),
      lucro: media(comFicha, (p) => p.lucro), cmvPct: media(comFicha, (p) => p.cmvPct),
      margin: media(comFicha, (p) => p.margin),
    };
  }
  return { ...base, porItem: true, semDados: true, unidades: 0, cmv: 0, venda: 0, lucro: 0, cmvPct: 0, margin: 0 };
}

/** Agrupa as linhas por categoria, ordena as categorias e os produtos de cada uma pela mesma coluna. */
export function agruparPorCategoria(linhas, sort) {
  const grupos = new Map();
  for (const l of linhas) {
    const nome = l.category || SEM_CATEGORIA;
    if (!grupos.has(nome)) grupos.set(nome, []);
    grupos.get(nome).push(l);
  }
  const categorias = [...grupos].map(([nome, produtos]) => resumirCategoria(nome, ordenar(produtos, sort)));
  // Sem dados (nenhum produto com ficha) vai sempre para o fim, qualquer que seja a ordem.
  const comDados = ordenar(categorias.filter((c) => !c.semDados), sort);
  return [...comDados, ...categorias.filter((c) => c.semDados)];
}
